import { z } from 'zod'

/**
 * A Page is a BlockNote document. `blocks` is the editor's JSON (opaque to the
 * sync layer), `text` the plain-text projection the editor keeps in step for
 * search, display names and the assistant.
 */
export const pageSpec = z
  .object({
    title: z.string().max(200).default(''),
    icon: z.string().max(40).optional(),
    /** built-in gradient cover name, see covers.ts */
    cover: z.string().max(32).optional(),
    blocks: z.array(z.unknown()).default([]),
    text: z.string().max(400_000).default(''),
    pinned: z.boolean().default(false),
    /** sibling order; lexicographic, client-assigned */
    rank: z.string().max(64).optional(),
    /** one-off reminder, RFC 3339 UTC */
    remind_at: z.string().optional(),
  })
  .strict()
export type PageSpec = z.infer<typeof pageSpec>

export const pageStatus = z.object({ last_touched: z.string(), reminder_fired_for: z.string().optional() })
export type PageStatus = z.infer<typeof pageStatus>

/** Title, else the first line of the text, else Untitled (mirrors Go DisplayName). */
export const pageDisplayName = (spec: PageSpec): string => {
  const t = (spec.title ?? '').trim()
  if (t) return t
  const first = (spec.text.split('\n')[0] ?? '').trim()
  if (!first) return 'Untitled'
  const chars = Array.from(first)
  return chars.length > 80 ? chars.slice(0, 77).join('') + '…' : first
}

/** Next rank after the given siblings: zero-padded so lexicographic order is numeric order. */
export const nextRank = (ranks: (string | undefined)[]): string => {
  const max = ranks.reduce((m, r) => Math.max(m, Number(r ?? 0) || 0), 0)
  return String(Math.floor(max) + 1).padStart(6, '0')
}
